import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../lib/usePageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEARCH_INTENTS = ['Informational', 'Commercial', 'Navigational', 'Transactional'];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    { "@type": "Question", "name": "What is an AI SEO content brief generator?", "acceptedAnswer": { "@type": "Answer", "text": "An AI SEO content brief generator is a tool that automatically creates a structured outline for blog posts based on a target keyword, search intent, and niche. It helps writers and marketers plan SEO-friendly content faster." } },
    { "@type": "Question", "name": "Can I create a social media post using this AI content brief generator?", "acceptedAnswer": { "@type": "Answer", "text": "No. The tool is meant to create blog content outlines only." } },
    { "@type": "Question", "name": "How many briefs can I generate for free?", "acceptedAnswer": { "@type": "Answer", "text": "You can generate up to three SEO content briefs per day for free. This allows you to quickly test the tool before deciding to use it more frequently." } },
    { "@type": "Question", "name": "Does the tool generate full articles or only outlines?", "acceptedAnswer": { "@type": "Answer", "text": "This tool focuses on generating SEO-optimized content briefs and outlines. The brief helps writers create higher-quality articles that match search intent." } },
    { "@type": "Question", "name": "Can agencies use this tool for client SEO projects?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Agencies can use the generator to create structured briefs for writers, streamline content workflows, and maintain consistent SEO standards across projects." } }
  ]
};

const faqs = [
  { q: "What is an AI SEO content brief generator?", a: "An AI SEO content brief generator is a tool that automatically creates a structured outline for blog posts based on a target keyword, search intent, and niche. It helps writers and marketers plan SEO-friendly content faster." },
  { q: "Can I create a social media post using this AI content brief generator?", a: "No. The tool is meant to create BLOG content outlines as of now." },
  { q: "How many briefs can I generate for free?", a: "You can generate up to three SEO content briefs per day for free. This allows you to quickly test the tool before deciding to use it more frequently." },
  { q: "Does the tool generate full articles or only outlines?", a: "This tool focuses on generating SEO-optimized content briefs and outlines. The brief helps writers create higher-quality articles that match search intent." },
  { q: "Can agencies use this tool for client SEO projects?", a: "Yes. Agencies can use the generator to create structured briefs for writers, streamline content workflows, and maintain consistent SEO standards across projects." },
];

function getSessionId() {
  let id = sessionStorage.getItem('wricoo_session_id');
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem('wricoo_session_id', id);
  }
  return id;
}

/* ────────────── Markdown → HTML Renderer ────────────── */
function renderMarkdownToHtml(md) {
  if (!md) return '';
  // Strip "Helpful Notes for Writer:" labels
  let text = md.replace(/\*?\*?Helpful Notes for Writer:?\*?\*?\s*/gi, '');

  const lines = text.split('\n');
  let html = '';
  let inList = false;
  let inOl = false;
  let olCounter = 0;

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Bold inline
    line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Italic inline
    line = line.replace(/\*(.+?)\*/g, '<em>$1</em>');

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      if (inList) { html += '</ul>'; inList = false; }
      if (inOl) { html += '</ol>'; inOl = false; }
      html += '<hr style="border:0;border-top:1px solid #e5e7eb;margin:16px 0" />';
      continue;
    }

    // Headings
    if (line.startsWith('### ')) {
      if (inList) { html += '</ul>'; inList = false; }
      if (inOl) { html += '</ol>'; inOl = false; }
      html += `<h3>${line.slice(4).trim().replace(/^\*{0,2}H\d:\s*/i, '')}</h3>`;
      continue;
    }
    if (line.startsWith('## ')) {
      if (inList) { html += '</ul>'; inList = false; }
      if (inOl) { html += '</ol>'; inOl = false; }
      html += `<h2>${line.slice(3).trim().replace(/^\*{0,2}H\d:\s*/i, '')}</h2>`;
      continue;
    }
    if (line.startsWith('# ')) {
      if (inList) { html += '</ul>'; inList = false; }
      if (inOl) { html += '</ol>'; inOl = false; }
      html += `<h1>${line.slice(2).trim().replace(/^\*{0,2}H\d:\s*/i, '')}</h1>`;
      continue;
    }

    // Bullet list
    if (/^[-*]\s/.test(line.trim())) {
      if (inOl) { html += '</ol>'; inOl = false; }
      if (!inList) { html += '<ul>'; inList = true; }
      html += `<li>${line.trim().replace(/^[-*]\s+/, '')}</li>`;
      continue;
    }

    // Numbered list
    if (/^\d+\.\s/.test(line.trim())) {
      if (inList) { html += '</ul>'; inList = false; }
      if (!inOl) { html += '<ol>'; inOl = true; olCounter = 0; }
      olCounter++;
      html += `<li>${line.trim().replace(/^\d+\.\s+/, '')}</li>`;
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      if (inList) { html += '</ul>'; inList = false; }
      if (inOl) { html += '</ol>'; inOl = false; }
      continue;
    }

    // Normal paragraph
    if (inList) { html += '</ul>'; inList = false; }
    if (inOl) { html += '</ol>'; inOl = false; }
    html += `<p>${line.trim()}</p>`;
  }

  if (inList) html += '</ul>';
  if (inOl) html += '</ol>';
  return html;
}

/* ────────────── Brief Output Component ────────────── */
function BriefOutput({ markdown }) {
  const [copied, setCopied] = useState(false);
  const outputRef = useRef(null);
  const html = renderMarkdownToHtml(markdown);

  const handleCopy = async () => {
    try {
      const htmlBlob = new Blob([html], { type: 'text/html' });
      const textBlob = new Blob([markdown], { type: 'text/plain' });
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: copy plain text
      try {
        await navigator.clipboard.writeText(markdown);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch { /* ignore */ }
    }
  };

  return (
    <div className="mt-12 relative" data-testid="brief-output">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold text-gray-900">Generated Content Brief</h2>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition"
          data-testid="copy-btn"
        >
          {copied ? <><Check className="w-4 h-4 text-emerald-600" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
        </button>
      </div>
      <div
        ref={outputRef}
        className="brief-output bg-white border border-gray-200 rounded-[3px] p-6"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

/* ────────────── Intent Selector ────────────── */
function IntentSelector({ selected, onChange }) {
  const toggle = (intent) => {
    if (selected.includes(intent)) {
      onChange(selected.filter(i => i !== intent));
    } else {
      onChange([...selected, intent]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2" data-testid="intent-selector">
      {SEARCH_INTENTS.map(intent => (
        <button
          key={intent}
          type="button"
          onClick={() => toggle(intent)}
          className={`px-3 py-1.5 text-sm rounded-md border transition ${
            selected.includes(intent)
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
          }`}
          data-testid={`intent-${intent.toLowerCase()}`}
        >
          {intent}
        </button>
      ))}
    </div>
  );
}

/* ────────────── Main Page ────────────── */
export default function AiSeoContentBriefGenerator() {
  const navigate = useNavigate();
  const sessionId = useRef(getSessionId());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [remaining, setRemaining] = useState(null);
  const pollRef = useRef(null);

  const [form, setForm] = useState({
    niche: '',
    primaryKeyword: '',
    secondaryKeywords: '',
    searchIntent: [],
    businessDetails: '',
    location: '',
    icp: '',
    caseFormatting: 'Sentence Case',
  });

  usePageMeta({
    title: 'AI SEO Content Brief Generator - Wricoo',
    description: 'Generate SEO-ready content briefs in seconds with our AI SEO Content Brief Generator. Define keywords, intent, and audience to create structured outlines.',
    robots: 'index, follow',
    canonical: 'https://wricoo.com/tools/ai-seo-content-brief-generator/',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema-ai-brief';
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('faq-schema-ai-brief');
      if (el) el.remove();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/brief-usage/${sessionId.current}`)
      .then(r => r.json())
      .then(d => setRemaining(d.remaining))
      .catch(() => {});
  }, []);

  const updateField = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const validate = () => {
    if (!form.niche.trim()) return 'Please enter a niche.';
    if (form.primaryKeyword.trim().split(/\s+/).length < 2) return 'Primary keyword must be more than 2 words.';
    if (form.searchIntent.length === 0) return 'Please select at least one search intent.';
    if (!form.businessDetails.trim()) return 'Please enter client business details.';
    if (!form.icp.trim()) return 'Please enter the ideal customer profile.';
    return null;
  };

  const pollForResult = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/brief-status/${jobId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setResult(data.result);
          setLoading(false);
        } else if (data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setError(data.error || 'Failed to generate brief. Please try again.');
          setLoading(false);
        }
      } catch {
        // Keep polling on network errors
      }
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setResult('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/generate-brief`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          searchIntent: form.searchIntent.join(', '),
          session_id: sessionId.current,
        }),
      });

      if (res.status === 403) {
        const data = await res.json();
        setError(data.detail || 'Daily limit reached. You can generate up to 3 briefs per day.');
        setRemaining(0);
        setLoading(false);
        return;
      }
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || 'Something went wrong. Please try again.');
        setLoading(false);
        return;
      }

      const data = await res.json();
      setRemaining(data.remaining);
      // Start polling for the result
      pollForResult(data.job_id);
    } catch {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="ai-brief-generator-page">

      {/* Output CSS */}
      <style>{`
        .brief-output { color: #111; font-size: 16px; line-height: 1.7; }
        .brief-output h1 { font-size: 22px; font-weight: 700; margin: 24px 0 8px; line-height: 1.3; }
        .brief-output h2 { font-size: 20px; font-weight: 700; margin: 20px 0 6px; line-height: 1.3; }
        .brief-output h3 { font-size: 18px; font-weight: 600; margin: 16px 0 4px; line-height: 1.3; }
        .brief-output p { margin: 6px 0; }
        .brief-output ul { margin: 6px 0 12px 20px; list-style: disc; }
        .brief-output ol { margin: 6px 0 12px 20px; list-style: decimal; }
        .brief-output li { margin: 3px 0; }
        .brief-output strong { font-weight: 700; }
      `}</style>

      {/* Header */}
      <header className="border-b border-blue-200 bg-blue-600 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/tools/" className="text-blue-200 hover:text-white transition"><ArrowLeft className="w-5 h-5" /></Link>
            <Link to="/" className="text-xl font-bold text-white tracking-tight">Wricoo</Link>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/about/" className="text-sm font-medium text-blue-100 hover:text-white transition">About</Link>
            <Link to="/pricing/" className="text-sm font-medium text-blue-100 hover:text-white transition">Pricing</Link>
            <Link to="/tools/" className="text-sm font-medium text-blue-100 hover:text-white transition">Tools</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white hover:bg-blue-500 hover:text-white" onClick={() => navigate('/login/')}>Sign In</Button>
            <Button size="sm" className="bg-white text-blue-600 hover:bg-blue-50" onClick={() => navigate('/register/')}>Get Started</Button>
          </div>
        </div>
      </header>

      {/* Tool Section */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h1 className="font-bold text-gray-900 leading-tight mb-4 text-center">AI SEO Content Brief Generator</h1>

          {/* Internal Links */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            <a href="/tools/character-count-online/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">Character Count Online</a>
            <a href="/tools/word-count-online/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">Word Counter Online</a>
            <a href="/tools/case-converter-online/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">Case Converter Online</a>
            <a href="/tools/remove-extra-spaces-online/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">Remove Extra Spaces Online</a>
          </div>

          {remaining !== null && (
            <div className="mb-8 text-center">
              <span className={`text-sm font-medium px-3 py-1 rounded-full ${remaining > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`} data-testid="usage-badge">
                {remaining > 0 ? `${remaining} brief${remaining !== 1 ? 's' : ''} remaining today` : 'Daily limit reached (3/3 used)'}
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="brief-form">

            {/* Niche */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Niche</label>
              <input
                type="text"
                value={form.niche}
                onChange={e => updateField('niche', e.target.value)}
                placeholder="e.g. B2B SaaS, Healthcare, E-commerce"
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="input-niche"
              />
            </div>

            {/* Primary Keyword */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Keyword</label>
              <input
                type="text"
                value={form.primaryKeyword}
                onChange={e => updateField('primaryKeyword', e.target.value)}
                placeholder="e.g. best project management tools for remote teams"
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="input-primary-keyword"
              />
              {form.primaryKeyword && form.primaryKeyword.trim().split(/\s+/).length < 2 && (
                <p className="text-red-500 text-xs mt-1">Provide more than 2 words.</p>
              )}
            </div>

            {/* Secondary Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Keywords <span className="text-gray-400">(up to 3, comma-separated)</span></label>
              <input
                type="text"
                value={form.secondaryKeywords}
                onChange={e => updateField('secondaryKeywords', e.target.value)}
                placeholder="e.g. remote team tools, project tracking software, team collaboration apps"
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="input-secondary-keywords"
              />
            </div>

            {/* Search Intent */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search Intent <span className="text-gray-400">(multiple selection available)</span></label>
              <IntentSelector selected={form.searchIntent} onChange={v => updateField('searchIntent', v)} />
            </div>

            {/* Client Business Details */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Business Details</label>
              <textarea
                value={form.businessDetails}
                onChange={e => { if (e.target.value.length <= 400) updateField('businessDetails', e.target.value); }}
                placeholder="My client operates in the B2B SaaS [ABC niche], helping [target audience] solve [XYZ problem] through features such as [Option A], [Option B], and [Option C]."
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="input-business-details"
              />
              <p className="text-xs text-gray-400 text-right mt-1">{form.businessDetails.length}/400</p>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location <span className="text-gray-400">(optional)</span></label>
              <input
                type="text"
                value={form.location}
                onChange={e => updateField('location', e.target.value)}
                placeholder="e.g. United States, London, UK"
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="input-location"
              />
            </div>

            {/* Ideal Customer Profile */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ideal Customer Profile (ICP)</label>
              <textarea
                value={form.icp}
                onChange={e => { if (e.target.value.length <= 300) updateField('icp', e.target.value); }}
                placeholder="The ideal customer is a [industry] company with [X-Y employees], where [role/team] is responsible for [function] and struggles with [problem]. They are looking for solutions that help them [desired outcome]."
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="input-icp"
              />
              <p className="text-xs text-gray-400 text-right mt-1">{form.icp.length}/300</p>
            </div>

            {/* Case Formatting */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Case Formatting for Headings</label>
              <select
                value={form.caseFormatting}
                onChange={e => updateField('caseFormatting', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="select-case-formatting"
              >
                <option value="Sentence Case">Sentence Case</option>
                <option value="Title Case">Title Case</option>
              </select>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="error-message">
                {error}
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              size="lg"
              className="w-full h-12"
              disabled={loading || remaining === 0}
              data-testid="generate-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Brief...</>
              ) : (
                'Generate SEO Content Brief'
              )}
            </Button>
          </form>

          {/* Output */}
          {result && <BriefOutput markdown={result} />}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-bold text-gray-900 mb-4">Manage SEO and Content in One Place</h2>
          <p className="text-gray-600 leading-relaxed mb-8 max-w-2xl mx-auto">Why juggle multiple spreadsheets, docs, and project management tools? Try Wricoo, a platform that allows SEO and content to collaborate in one place for everything.</p>
          <Button size="lg" className="h-12 px-8" onClick={() => navigate('/pricing/')}>
            Start Your 30-Day Free Trial. No Credit Card Needed. <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="font-bold text-gray-900 mb-8">AI SEO Content Brief Generator FAQs</h2>
          <div className="space-y-8">
            {faqs.map((faq, i) => (
              <div key={i} data-testid={`faq-item-${i}`}>
                <h3 className="text-base font-semibold text-gray-900 mb-2">Q. {faq.q}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Wricoo. All rights reserved.</p>
          <nav className="flex gap-4 text-sm text-gray-500">
            <a href="/about/" className="hover:text-gray-900 transition">About</a>
            <a href="/pricing/" className="hover:text-gray-900 transition">Pricing</a>
            <a href="/tools/" className="hover:text-gray-900 transition">Tools</a>
            <a href="/privacy-policy/" className="hover:text-gray-900 transition">Privacy Policy</a>
            <a href="/terms-and-conditions/" className="hover:text-gray-900 transition">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
